import { useRootStore } from 'stores/RootStore'
import { useModals } from 'almanac-ui/Modal'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { IntegrationInitializationDrawer } from 'components/IntegrationInitialization/IntegrationInitializationDrawer'
import { IntegrationInitializationModal } from 'components/IntegrationInitialization/IntegrationInitializationModal'
import { useConnectAccountDialogProps } from 'hooks/useConnectAccountDialog/Types'
import { InitializationDrawer } from 'components/Onboarding/CreateBrandStyleModal/InitializationDrawer'
import {
  Facebook,
  Google,
  Instagram,
  Mailchimp,
  Twitter,
  Wix,
  YouTube,
  GoHighLevel,
  GoogleDrive,
  Shopify,
} from 'blaze-ui/icons'
import { FacebookNotice } from 'components/IntegrationInitialization/FacebookInitialization/FacebookInitialization'
import { GoHighLevelNotice } from 'components/IntegrationInitialization/GoHighLevelInitialization/GoHighLevelInitialization'
import { GoogleDriveNotice } from 'components/IntegrationInitialization/GoogleDriveInitialization/GoogleDriveInitialization'
import { useCallback, useMemo, useRef } from 'react'
import { absurd } from 'types'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { InstagramNotice } from 'components/IntegrationInitialization/InstagramInitialization/InstagramInitialization'
import { LinkedIn } from 'almanac-ui/icons'
import { LinkedInNotice } from 'components/IntegrationInitialization/LinkedinInitialization/LinkedinInitialization'
import {
  GoogleBusinessProfileNotice,
  GoogleNotice,
} from 'components/IntegrationInitialization/GoogleInitialization/GoogleInitialization'
import { WordPressInitialization } from 'components/IntegrationInitialization'
import { WordPressInitializationDrawerContent } from 'components/IntegrationInitialization/WordPressInitialization/WordPressInitialization'
import { MailchimpNotice } from 'components/IntegrationInitialization/MailchimpInitialization/MailchimpInitialization'
import { YoutubeNotice } from 'components/IntegrationInitialization/YouTubeInitialization/YouTubeInitialization'
import { WordPressBrand } from 'blaze-ui/icons/35'
import { MetaAdsNotice } from 'components/IntegrationInitialization/MetaAdsInitialization/MetaAdsInitialization'
import { MetaBrand } from 'blaze-ui/icons/20/MetaBrand'
import { GoogleAdsNotice } from 'components/IntegrationInitialization/GoogleAdsInitialization/GoogleAdsInitialization'
import { ShopifyNotice } from 'components/IntegrationInitialization/ShopifyInitialization/ShopifyInitialization'
import { ShopifyInitialization } from 'components/IntegrationInitialization/ShopifyInitialization/ShopifyInitialization'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { WixNotice } from 'components/IntegrationInitialization/WixInitialization/WixInitialization'
import { XNotice } from 'components/IntegrationInitialization/XInitialization/XInitialization'

export const useConnectAccountDialog = ({
  provider,
  switchFromIntegrationId,
  onConnect,
}: useConnectAccountDialogProps) => {
  const { openDrawer, closeDrawer } = useRootStore().drawersStore
  const { openModal, closeModal, closeStack } = useModals()
  const { isMobile } = useResponsiveDesign()
  const integrationRef = useRef<ExternalIntegration | undefined>()

  const { data: externalIntegrations } = useExternalIntegrations({
    enabled: !!switchFromIntegrationId,
  })

  const commonProps = useMemo(() => {
    if (provider === ExternalIntegrationEnum.WordPress) {
      return {
        providerName: 'WordPress',
        icon: <WordPressBrand size={32} />,
        title: 'Connect WordPress',
        primaryButtonText: 'Go to WordPress',
      }
    }

    type DialogIntegrationEnum = Exclude<
      ExternalIntegrationEnum,
      ExternalIntegrationEnum.WordPress | ExternalIntegrationEnum.TikTok | ExternalIntegrationEnum.Zapier
    >
    const dialogProvider = provider as DialogIntegrationEnum
    switch (dialogProvider) {
      case ExternalIntegrationEnum.Facebook:
        return {
          notice: <FacebookNotice />,
          providerName: 'Facebook (Meta)',
          icon: <Facebook size={32} />,
          title: 'Connect Facebook Business Account',
          primaryButtonText: 'Go to Facebook Business',
        }
      case ExternalIntegrationEnum.Instagram:
        return {
          notice: <InstagramNotice />,
          providerName: 'Facebook (Meta)',
          icon: <Instagram size={32} />,
          title: 'Connect Instagram Business Account',
          primaryButtonText: 'Go to Facebook Business',
        }
      case ExternalIntegrationEnum.LinkedIn:
        return {
          notice: <LinkedInNotice />,
          providerName: 'LinkedIn',
          icon: <LinkedIn size={32} />,
          title: 'Connect to LinkedIn',
          primaryButtonText: 'Go to Connect LinkedIn',
        }
      case ExternalIntegrationEnum.X:
        return {
          notice: <XNotice />,
          providerName: 'X/Twitter',
          icon: <Twitter size={32} />,
          title: 'Connect X/Twitter Account',
          primaryButtonText: 'Go to X/Twitter',
        }
      case ExternalIntegrationEnum.Google:
        return {
          notice: <GoogleNotice />,
          providerName: 'Google',
          icon: <Google size={32} />,
          title: 'Connect Google Account',
          primaryButtonText: 'Go to Google',
        }
      case ExternalIntegrationEnum.GoogleBusinessProfile:
        return {
          notice: <GoogleBusinessProfileNotice />,
          providerName: 'Google Business Profile',
          icon: <Google size={32} />,
          title: 'Connect Google Business Profile',
          primaryButtonText: 'Go to Google',
        }
      case ExternalIntegrationEnum.YouTube:
        return {
          notice: <YoutubeNotice />,
          providerName: 'YouTube',
          icon: <YouTube size={32} />,
          title: 'Connect YouTube Account',
          primaryButtonText: 'Go to YouTube',
        }
      case ExternalIntegrationEnum.Mailchimp:
        return {
          notice: <MailchimpNotice />,
          providerName: 'Mailchimp',
          icon: <Mailchimp size={32} />,
          title: 'Connect Mailchimp Account',
          primaryButtonText: 'Go to Mailchimp',
        }
      case ExternalIntegrationEnum.MetaAds:
        return {
          notice: <MetaAdsNotice />,
          providerName: 'Meta Ads',
          icon: <MetaBrand size={32} />,
          title: 'Connect Meta Ads Account',
          primaryButtonText: 'Go to Meta Ads',
        }
      case ExternalIntegrationEnum.GoHighLevel:
        return {
          notice: <GoHighLevelNotice />,
          providerName: 'GoHighLevel',
          icon: <GoHighLevel size={32} />,
          title: 'Connect GoHighLevel Account',
          primaryButtonText: 'Go to GoHighLevel',
        }
      case ExternalIntegrationEnum.Wix:
        return {
          notice: <WixNotice />,
          providerName: 'Wix',
          icon: <Wix size={32} />,
          title: 'Connect Wix Account',
          primaryButtonText: 'Go to Wix',
        }
      case ExternalIntegrationEnum.Shopify:
        return {
          notice: <ShopifyNotice />,
          providerName: 'Shopify',
          icon: <Shopify size={32} />,
          title: 'Connect Shopify Account',
          primaryButtonText: 'Go to Shopify',
        }
      case ExternalIntegrationEnum.GoogleDrive:
        return {
          notice: <GoogleDriveNotice />,
          providerName: 'Google Drive',
          icon: <GoogleDrive size={32} />,
          title: 'Connect Google Drive Account',
          primaryButtonText: 'Connect Google Drive',
        }
      case ExternalIntegrationEnum.GoogleAds:
        return {
          notice: <GoogleAdsNotice />,
          providerName: 'Google Ads',
          icon: <Google size={32} />,
          title: 'Connect Google Ads Account',
          primaryButtonText: 'Go to Google',
        }
      default:
        absurd(dialogProvider)
        throw Error(`Unsupported provider ${dialogProvider} for integration dialog`)
    }
  }, [provider])

  const openDialog = useCallback(() => {
    if (provider === ExternalIntegrationEnum.WordPress) {
      integrationRef.current = undefined
      if (isMobile) {
        return openDrawer(InitializationDrawer, {
          component: () => (
            <WordPressInitializationDrawerContent
              onConnect={(integration?: ExternalIntegration) => {
                integrationRef.current = integration
                onConnect?.(provider, { createdIntegration: integration })
              }}
              switchFromIntegrationId={switchFromIntegrationId}
            />
          ),
        })
      }
      return openModal(WordPressInitialization, {
        onConnect: (integration?: ExternalIntegration) => {
          integrationRef.current = integration
          onConnect?.(provider, { createdIntegration: integration })
        },
        switchFromIntegrationId,
      })
    }

    if (provider === ExternalIntegrationEnum.Shopify) {
      const switchFromIntegration = externalIntegrations?.external_integrations.find(
        (integration) => integration.id === switchFromIntegrationId,
      )
      const switchFromPlatform = switchFromIntegration?.provider
      return openModal(ShopifyInitialization, {
        onConnect: (shopDomain: string) => {
          onConnect?.(provider, { shopDomain })
          closeStack()
        },
        switchFromIntegrationId,
        switchFromPlatform,
      })
    }

    if (isMobile) {
      return openDrawer(InitializationDrawer, {
        component: () => (
          <IntegrationInitializationDrawer
            {...commonProps}
            onConfirm={() => {
              onConnect?.(provider)
              closeDrawer()
            }}
          />
        ),
      })
    }

    return openModal(
      IntegrationInitializationModal,
      {
        ...commonProps,
        onConfirm: () => {
          onConnect?.(provider)
          closeStack()
        },
        onClose: () => {
          closeStack()
        },
        onPressOutside: () => {
          closeStack()
        },
      },
      { newStack: true },
    )
  }, [
    provider,
    isMobile,
    openModal,
    commonProps,
    switchFromIntegrationId,
    onConnect,
    externalIntegrations?.external_integrations,
    closeStack,
    openDrawer,
    closeDrawer,
  ])

  const closeDialog = isMobile ? closeDrawer : closeModal

  return {
    openDialog,
    closeDialog,
  }
}
